<template>
  <div class="seat-editor d-flex ga-4">
    <nav class="palette d-flex flex-column ga-1" aria-label="Ferramentas">
      <div class="text-overline text-medium-emphasis px-2">
        Ferramentas
      </div>
      <v-tooltip
        v-for="item in TOOLS"
        :key="item.key"
        location="end"
        :text="item.tooltip"
        max-width="280"
      >
        <template #activator="{ props: tip }">
          <v-btn
            v-bind="tip"
            :variant="tool === item.key ? 'flat' : 'text'"
            :color="tool === item.key ? 'primary' : undefined"
            class="palette__btn text-none"
            block
            @click="tool = item.key"
          >
            <v-icon
              :icon="item.icon"
              size="small"
              class="palette__icon"
            />
            <span>{{ item.name }}</span>
            <kbd class="palette__kbd">{{ item.shortcut }}</kbd>
          </v-btn>
        </template>
      </v-tooltip>
      <div class="text-caption text-medium-emphasis px-2 mt-3">
        Duplo clique ou Enter sobre uma célula, em qualquer ferramenta, seleciona a célula e abre no painel.
      </div>
      <div class="text-caption text-medium-emphasis px-2 mt-2">
        <kbd class="palette__kbd palette__kbd--inline">Ctrl/⌘ Z</kbd> desfaz.
      </div>
    </nav>

    <div class="main flex-grow-1">
      <div class="options-bar d-flex align-center flex-wrap ga-3 px-3 py-2 mb-3">
        <div class="d-flex align-center ga-2 flex-0-0">
          <v-icon :icon="activeTool.icon" size="small" />
          <strong>{{ activeTool.name }}</strong>
        </div>
        <v-divider vertical class="my-1" />

        <template v-if="tool === 'type'">
          <v-chip
            v-for="(type, index) in seatTypes"
            :key="type"
            size="small"
            :color="SeatTypeColors[type]"
            :variant="paintType === type ? 'flat' : 'outlined'"
            @click="paintType = type"
          >
            <i v-if="SeatTypeIcons[type]" :class="[SeatTypeIcons[type], 'mr-1']" />
            {{ SeatTypeLabels[type] }}
            <kbd class="options-bar__kbd">{{ index + 1 }}</kbd>
          </v-chip>
        </template>

        <template v-else-if="tool === 'number'">
          <v-select
            v-model="pattern"
            :items="PATTERN_ITEMS"
            label="Padrão"
            variant="outlined"
            density="compact"
            hide-details
            style="flex: 0 0 300px"
          />
          <v-text-field
            v-model.number="startNumber"
            type="number"
            label="Começa em"
            variant="outlined"
            density="compact"
            hide-details
            style="flex: 0 0 100px"
          />
          <v-checkbox
            v-model="padNames"
            label="Dois dígitos (01)"
            density="compact"
            hide-details
            class="flex-0-0"
          />
        </template>

        <template v-else-if="tool === 'add'">
          <v-checkbox
            v-model="padNames"
            label="Dois dígitos (01)"
            density="compact"
            hide-details
            class="flex-0-0"
          />
          <span class="options-bar__hint">{{ activeTool.hint }}</span>
        </template>

        <span v-else class="options-bar__hint">{{ activeTool.hint }}</span>

        <v-spacer />
        <v-tooltip location="bottom" :text="undoTooltip">
          <template #activator="{ props: tip }">
            <span v-bind="tip">
              <v-btn
                size="small"
                variant="outlined"
                class="text-none"
                prepend-icon="fa-solid fa-rotate-left"
                :disabled="!editor.history.length"
                @click="undo"
              >
                Desfazer
              </v-btn>
            </span>
          </template>
        </v-tooltip>
        <div class="options-bar__last" :title="lastAction">
          <span class="text-medium-emphasis">Última ação:</span> {{ lastAction }}
        </div>
      </div>

      <v-alert
        v-if="message"
        :type="message.kind"
        variant="tonal"
        density="compact"
        closable
        class="mb-3"
        @click:close="message = null"
      >
        {{ message.text }}
      </v-alert>

      <v-alert
        v-if="!editor.rows.length"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        Esta seção não tem filas. Escolha Adicionar e pinte a linha "nova" para criar a primeira.
        <template #append>
          <v-btn
            size="small"
            variant="text"
            @click="tool = 'add'"
          >
            Escolher Adicionar
          </v-btn>
        </template>
      </v-alert>

      <div class="grid-wrap">
        <div
          class="grid"
          :class="`grid--${tool}`"
          :style="gridStyle"
          @pointerleave="hover = null"
        >
          <div />
          <div class="stage" :style="{ gridColumn: `span ${colCount}` }">
            PALCO
          </div>
          <div />

          <div />
          <div
            v-for="col in colList"
            :key="`h-${col}`"
            class="col-head"
            :class="{
              'col-head--gap': tool === 'gap',
              'col-head--hover': tool === 'gap' && hoverHeader === col,
              'col-head--extra': col === colCount,
            }"
            @pointerenter="hoverHeader = col"
            @pointerleave="hoverHeader = null"
            @click="onHeaderClick($event, col)"
          >
            {{ col }}
          </div>
          <div />

          <template v-for="line in lines" :key="line.id">
            <button
              type="button"
              class="row-symbol"
              :class="{ 'row-symbol--ghost': line.ghost }"
              :title="line.ghost ? 'Criar uma fila vazia no fundo' : `Fila ${line.symbol}: renomear, mover ou remover`"
              @click="onSymbolClick($event, line.id)"
            >
              {{ line.ghost ? '+' : line.symbol }}
            </button>

            <div
              v-for="cell in line.cells"
              :key="`${line.id}-${cell.col}`"
              class="cell"
              :class="cellClass(line.id, cell.col, cell.seat, line.ghost)"
              :style="cellStyle(cell.seat)"
              @pointerdown="onCellDown($event, line.id, cell.col)"
              @pointerenter="onCellEnter($event, line.id, cell.col)"
              @click="onCellClick($event, line.id, cell.col)"
              @dblclick="openInPanel(line.id, cell.col)"
            >
              <span v-if="previewName(line.id, cell.col)" class="cell__name cell__name--preview">
                {{ previewName(line.id, cell.col) }}
              </span>
              <template v-else-if="cell.seat">
                <span class="cell__name">{{ cell.seat.name }}</span>
                <i
                  v-if="SeatTypeIcons[shownType(cell.seat)]"
                  :class="[SeatTypeIcons[shownType(cell.seat)], 'cell__type']"
                  :style="{ color: SeatTypeColors[shownType(cell.seat)] }"
                />
                <span v-if="cell.seat.label" class="cell__dot" />
              </template>
            </div>

            <button
              v-if="line.ghost"
              type="button"
              class="row-symbol row-symbol--ghost row-symbol--ghost-label"
              title="Criar uma fila vazia no fundo"
              @click="onSymbolClick($event, line.id)"
            >
              nova
            </button>
            <button
              v-else
              type="button"
              class="row-symbol"
              :title="`Fila ${line.symbol}: renomear, mover ou remover`"
              @click="onSymbolClick($event, line.id)"
            >
              {{ line.symbol }}
            </button>
          </template>
        </div>
      </div>

      <div class="status d-flex align-center flex-wrap ga-4 mt-3">
        <span class="status__hover">{{ hoverInfo }}</span>
        <v-spacer />
        <span v-for="type in seatTypes" :key="type" class="legend">
          <span class="legend__swatch" :style="{ borderColor: SeatTypeColors[type], background: `${SeatTypeColors[type]}22` }">
            <i v-if="SeatTypeIcons[type]" :class="SeatTypeIcons[type]" :style="{ color: SeatTypeColors[type] }" />
          </span>
          {{ SeatTypeLabels[type] }}
        </span>
        <span class="legend"><span class="cell__dot cell__dot--legend" /> tem rótulo</span>
        <span class="legend"><span class="legend__swatch legend__swatch--new" /> não salva</span>
      </div>
    </div>

    <seat-editor-panel
      ref="panel"
      :editor="editor"
      :cells="selectedCells"
      :title="title"
      :digits="digits"
      @select="selectOne"
      @clear="clearSelection"
      @shift="shiftSelection"
      @done="done"
      @fail="fail"
    />

    <v-menu
      v-model="rowMenuOpen"
      :target="rowMenuTarget"
      location="end"
      :close-on-content-click="false"
    >
      <v-card v-if="menuRow" width="260" class="pa-3">
        <div class="text-caption text-medium-emphasis mb-2">
          Fila {{ menuRow.symbol }} · linha {{ menuRow.order }} de {{ editor.rows.length }} · {{ plural(menuRow.seats.length, 'cadeira', 'cadeiras') }}
        </div>
        <v-text-field
          v-model="rowSymbol"
          label="Identificador"
          variant="outlined"
          density="compact"
          hide-details
          autofocus
          class="mb-2"
          @keydown.enter="renameMenuRow"
        >
          <template #append-inner>
            <v-btn
              size="x-small"
              variant="text"
              icon="fa-solid fa-check"
              :disabled="rowSymbol.trim().toUpperCase() === menuRow.symbol"
              @click="renameMenuRow"
            />
          </template>
        </v-text-field>
        <v-list density="compact" class="pa-0">
          <v-list-item
            prepend-icon="fa-solid fa-arrow-up"
            title="Mover para a frente"
            :disabled="menuRow.order === 1"
            @click="moveMenuRow(-1)"
          />
          <v-list-item
            prepend-icon="fa-solid fa-arrow-down"
            title="Mover para trás"
            :disabled="menuRow.order === editor.rows.length"
            @click="moveMenuRow(1)"
          />
          <v-list-item
            prepend-icon="fa-solid fa-plus"
            title="Inserir fila à frente"
            @click="insertRowNear(0)"
          />
          <v-list-item
            prepend-icon="fa-solid fa-plus"
            title="Inserir fila atrás"
            @click="insertRowNear(1)"
          />
          <v-list-item
            prepend-icon="fa-solid fa-object-group"
            title="Selecionar a fila inteira"
            @click="selectMenuRow"
          />
          <v-divider class="my-1" />
          <v-list-item
            prepend-icon="fa-solid fa-trash"
            title="Remover fila"
            base-color="error"
            @click="removeMenuRow"
          />
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
// The admin's seat editor (IDV-704): a tool palette, the Section's grid and a side panel for the selection. Every
// change goes through `editor`, one undoable step per click, stroke or bulk action.
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch, type CSSProperties, type Ref } from 'vue'
import {
  fullLabel,
  isNew,
  numberSeries,
  plural,
  rowDigits,
  type GridCell,
  type NumberingPattern,
  type SeatCell,
  type SeatEditor,
} from '@/helpers/seatEditor/editor'
import { SeatType, SeatTypeColors, SeatTypeIcons, SeatTypeLabels } from '@/constants/SeatTypes'
import type { GridRow, GridSeat } from '@/types/SectionGrid'
import { useDialog } from '@/stores/dialog'
import SeatEditorPanel from './seat-editor-panel.vue'
import { PATTERN_ITEMS, TOOLS, type Tool, type ToolDef } from './tools'

/** A cell an Adicionar, Apagar or Tipo stroke went over; applied on release, as one undo step */
interface PaintCell {
  rowId: string
  col: number
  seatId: string | null
}

interface Stroke {
  tool: Tool
  rowId: string
  fromCol: number
  lastCol: number
  seatId: string | null
  cells: PaintCell[]
  keys: Set<string>
}

interface Line {
  id: string
  symbol: string
  ghost: boolean
  cells: { col: number, seat: GridSeat | undefined }[]
}

/** A selected cell, held by ids: a seat by its id (it follows the seat when it moves), an empty cell by column */
interface Selected {
  rowId: string
  seatId: string | null
  col: number
}

interface CellRef {
  rowId: string
  col: number
}

interface Marquee {
  start: CellRef
  end: CellRef
  moved: boolean
  base: Selected[]
}

interface Message {
  text: string
  kind: 'error' | 'info'
}

interface Props {
  editor: SeatEditor
  /** "Setor › Seção" */
  title: string
}

const { editor, title } = defineProps<Props>()

defineOptions({ name: 'SeatEditor' })

const dialogStore = useDialog()

/** The "nova fila" line at the back, which creates a Row when painted */
const GHOST = '__ghost__'

const tool: Ref<Tool> = ref('select')
const seatTypes: SeatType[] = Object.values(SeatType)

const paintType: Ref<SeatType> = ref(SeatType.WHEELCHAIR)
const pattern: Ref<NumberingPattern> = ref('centre_odd_left')
const startNumber: Ref<number> = ref(1)
const padNames: Ref<boolean> = ref(true)
const lastAction: Ref<string> = ref('nenhuma')
const message: Ref<Message | null> = ref(null)
const hover: Ref<CellRef | null> = ref(null)
const hoverHeader: Ref<number | null> = ref(null)
const shiftHeld: Ref<boolean> = ref(false)
const stroke: Ref<Stroke | null> = ref(null)
const marquee: Ref<Marquee | null> = ref(null)
/** Set when a marquee drag ends, so the click that follows the release doesn't replace the selection */
let suppressClick = false

const panel = useTemplateRef<{ focusName: () => void, resetSeatForm: () => void }>('panel')

const activeTool = computed<ToolDef>(() => TOOLS.find(item => item.key === tool.value)!)

/** Zero-padding for names in an empty Row */
const digits = computed<number>(() => (padNames.value ? 2 : 1))

function done(text: string) {
  lastAction.value = text
  if (message.value?.kind === 'error') message.value = null
}

function fail(text: string) {
  message.value = { text, kind: 'error' }
}

/** Reports a change's outcome: the error, or what was done */
function report(error: string | null, text: string) {
  if (error) fail(error)
  else done(text)
}

// Grid --------------------------------------------------------------------------------------------------------------

/** The seats of each Row by column */
const seatMap = computed<Map<string, Map<number, GridSeat>>>(() => new Map(
  editor.rows.map(row => [row.id, new Map(row.seats.map(seat => [seat.position, seat]))]),
))

function seatAt(rowId: string, col: number): GridSeat | undefined {
  return seatMap.value.get(rowId)?.get(col)
}

function rowById(id: string): GridRow | undefined {
  return editor.rows.find(row => row.id === id)
}

/** The Section's columns plus one empty column on the right to grow into */
const colCount = computed<number>(() => Math.max(0, ...editor.rows.flatMap(row => row.seats.map(seat => seat.position))) + 1)
const colList = computed<number[]>(() => Array.from({ length: colCount.value }, (_, i) => i + 1))

const gridStyle = computed<Record<string, string>>(() => ({
  gridTemplateColumns: `36px repeat(${colCount.value}, 30px) 36px`,
}))

const lines = computed<Line[]>(() => [
  ...editor.rows.map(row => ({
    id: row.id,
    symbol: row.symbol,
    ghost: false,
    cells: colList.value.map(col => ({ col, seat: seatAt(row.id, col) })),
  })),
  { id: GHOST, symbol: '+', ghost: true, cells: colList.value.map(col => ({ col, seat: undefined })) },
])

function seriesFor(count: number): string[] {
  const start = Number.isFinite(startNumber.value) ? Math.floor(startNumber.value) : 1
  return numberSeries(count, pattern.value, start, digits.value)
}

// Selection ---------------------------------------------------------------------------------------------------------

const selection: Ref<Selected[]> = ref([])
const anchor: Ref<CellRef | null> = ref(null)

function resolve(entry: Selected): GridCell | null {
  const rowIndex = editor.rows.findIndex(row => row.id === entry.rowId)
  if (rowIndex < 0) return null
  const row = editor.rows[rowIndex]

  if (entry.seatId) {
    const seat = row.seats.find(s => s.id === entry.seatId)
    return seat ? { row, rowIndex, position: seat.position, seat } : null
  }
  return { row, rowIndex, position: entry.col, seat: seatAt(row.id, entry.col) }
}

const selectedCells = computed<GridCell[]>(() => selection.value.map(resolve).filter((cell): cell is GridCell => !!cell))
const selectedSeats = computed<SeatCell[]>(() => selectedCells.value.filter((cell): cell is SeatCell => !!cell.seat))
const selectedSeatIds = computed<Set<string>>(() => new Set(selectedSeats.value.map(cell => cell.seat.id)))
const selectedKeys = computed<Set<string>>(() => new Set(selectedCells.value.map(cell => `${cell.row.id}|${cell.position}`)))

function entryFor(rowId: string, col: number): Selected {
  return { rowId, seatId: seatAt(rowId, col)?.id ?? null, col }
}

function clearSelection() {
  selection.value = []
  anchor.value = null
}

/** Drops entries whose Row or seat no longer exists (after undo, a removal, a stroke) */
function prune() {
  selection.value = selection.value.filter(entry => resolve(entry))
  if (anchor.value && !rowById(anchor.value.rowId)) anchor.value = null
}

watch(() => editor.rows, prune, { deep: true })

function selectOne(rowId: string, col: number): boolean {
  if (!rowById(rowId)) {
    clearSelection()
    return false
  }
  selection.value = [ entryFor(rowId, col) ]
  anchor.value = { rowId, col }
  return true
}

/** The seats inside the rectangle between two cells (the "nova" line counts as the last Row) */
function rectEntries(a: CellRef, b: CellRef): Selected[] {
  const rows = editor.rows
  const index = (id: string): number => (id === GHOST ? rows.length - 1 : rows.findIndex(row => row.id === id))
  const i0 = index(a.rowId)
  const i1 = index(b.rowId)
  if (i0 < 0 || i1 < 0) return []

  const [r0, r1] = [Math.min(i0, i1), Math.max(i0, i1)]
  const [c0, c1] = [Math.min(a.col, b.col), Math.max(a.col, b.col)]
  return rows.slice(r0, r1 + 1).flatMap(row => row.seats
    .filter(seat => seat.position >= c0 && seat.position <= c1)
    .map(seat => ({ rowId: row.id, seatId: seat.id, col: seat.position })))
}

function seatEntriesOnly(): Selected[] {
  return selectedSeats.value.map(cell => ({ rowId: cell.row.id, seatId: cell.seat.id, col: cell.position }))
}

function union(base: Selected[], extra: Selected[]): Selected[] {
  const ids = new Set(base.map(entry => entry.seatId))
  return [...base, ...extra.filter(entry => !ids.has(entry.seatId))]
}

function selectClick(event: MouseEvent, rowId: string, col: number) {
  if (rowId === GHOST) {
    clearSelection()
    return
  }
  if (!rowById(rowId)) return

  if (event.shiftKey && anchor.value) {
    selection.value = rectEntries(anchor.value, { rowId, col })
    return
  }

  const seat = seatAt(rowId, col)
  if ((event.ctrlKey || event.metaKey) && seat) {
    const seats = seatEntriesOnly()
    selection.value = seats.some(entry => entry.seatId === seat.id)
      ? seats.filter(entry => entry.seatId !== seat.id)
      : [...seats, entryFor(rowId, col)]
    anchor.value = { rowId, col }
    return
  }

  selectOne(rowId, col)
}

function inMarquee(rowId: string, col: number): boolean {
  const m = marquee.value
  if (!m?.moved) return false

  const index = (id: string): number => (id === GHOST ? editor.rows.length : editor.rows.findIndex(row => row.id === id))
  const i = index(rowId)
  const [i0, i1] = [index(m.start.rowId), index(m.end.rowId)]
  return i >= Math.min(i0, i1) && i <= Math.max(i0, i1)
    && col >= Math.min(m.start.col, m.end.col) && col <= Math.max(m.start.col, m.end.col)
}

/** Double click or Enter: select the cell, switch to Selecionar and put the cursor on the Name field */
function openInPanel(rowId: string, col: number) {
  if (rowId === GHOST || !selectOne(rowId, col)) return
  tool.value = 'select'
  panel.value?.focusName()
}

/**
 * Shifts every selected seat `delta` columns within its own Row, as one undo step. Refused as a whole when a seat
 * would leave the grid or land on a seat that isn't selected.
 */
function shiftSelection(delta: number) {
  const ids = selectedSeats.value.map(cell => cell.seat.id)
  if (!ids.length || !delta) return

  const columns = plural(Math.abs(delta), 'coluna', 'colunas')
  report(editor.shiftSeats(ids, delta), `moveu ${plural(ids.length, 'cadeira', 'cadeiras')} ${columns} para a ${delta > 0 ? 'direita' : 'esquerda'}`)
}

function removeSelected() {
  const ids = selectedSeats.value.map(cell => cell.seat.id)
  if (!ids.length) return

  report(editor.removeSeats(ids), `removeu ${plural(ids.length, 'cadeira', 'cadeiras')}`)
}

// Undo --------------------------------------------------------------------------------------------------------------

const undoTooltip = computed<string>(() => {
  const last = editor.history.at(-1)
  return last ? `Desfazer: ${last.label} (Ctrl/⌘+Z)` : 'Nada para desfazer'
})

function undo() {
  stroke.value = null
  marquee.value = null

  const entry = editor.undo()
  if (entry) done(`desfez: ${entry.label}`)
}

// Cell rendering ----------------------------------------------------------------------------------------------------

function strokeRange(s: Stroke): [number, number] {
  return [Math.min(s.fromCol, s.lastCol), Math.max(s.fromCol, s.lastCol)]
}

/** Live names over the stroked cells while dragging with Numerar */
const numberPreview = computed<Map<number, string>>(() => {
  const map = new Map<number, string>()
  const s = stroke.value
  if (s?.tool !== 'number') return map

  const [from, to] = strokeRange(s)
  seriesFor(to - from + 1).forEach((name, i) => map.set(from + i, name))
  return map
})

/** Names the seats an Adicionar stroke will create, Row by Row in the order the cells were painted */
function addNames(s: Stroke | null): Map<string, string> {
  const map = new Map<string, string>()
  if (s?.tool !== 'add') return map

  const next = new Map<string, { number: number, width: number }>()
  s.cells.forEach((cell) => {
    if (!next.has(cell.rowId)) {
      const row = rowById(cell.rowId)
      next.set(cell.rowId, row
        ? { number: Number.parseInt(editor.suggestName(row.id, digits.value), 10), width: rowDigits(row, digits.value) }
        : { number: 1, width: digits.value })
    }
    const counter = next.get(cell.rowId)!
    map.set(`${cell.rowId}:${cell.col}`, String(counter.number).padStart(counter.width, '0'))
    counter.number++
  })
  return map
}

const addPreview = computed<Map<string, string>>(() => addNames(stroke.value))

const paintedSeats = computed<Set<string>>(() => {
  const s = stroke.value
  return new Set(s && (s.tool === 'erase' || s.tool === 'type') ? s.cells.map(cell => cell.seatId!) : [])
})

const movingSeat = computed<GridSeat | undefined>(() => {
  const s = stroke.value
  if (s?.tool !== 'move') return undefined
  return rowById(s.rowId)?.seats.find(seat => seat.id === s.seatId)
})

/** Mover started on a selected seat while several are selected: the whole selection follows */
const groupMove = computed<boolean>(() => {
  const s = stroke.value
  return s?.tool === 'move' && !!s.seatId && selectedSeatIds.value.has(s.seatId) && selectedSeats.value.length > 1
})

const moveDelta = computed<number>(() => {
  const s = stroke.value
  return s?.tool === 'move' ? s.lastCol - s.fromCol : 0
})

function moveTargetName(rowId: string, col: number): string | null {
  const s = stroke.value
  if (s?.tool !== 'move' || !moveDelta.value) return null

  if (groupMove.value) {
    return selectedSeats.value.find(cell => cell.row.id === rowId && cell.seat.position + moveDelta.value === col)?.seat.name ?? null
  }
  if (s.rowId !== rowId || !movingSeat.value) return null
  return col === s.lastCol && col !== movingSeat.value.position ? movingSeat.value.name : null
}

function previewName(rowId: string, col: number): string | null {
  const s = stroke.value
  if (!s) return null
  if (s.tool === 'number' && s.rowId === rowId) return numberPreview.value.get(col) ?? null
  if (s.tool === 'add') return addPreview.value.get(`${rowId}:${col}`) ?? null
  if (s.tool === 'move') return moveTargetName(rowId, col)
  return null
}

function shownType(seat: GridSeat): SeatType {
  return stroke.value?.tool === 'type' && paintedSeats.value.has(seat.id) ? paintType.value : seat.type
}

function cellStyle(seat: GridSeat | undefined): CSSProperties | undefined {
  return seat ? { '--seat-color': SeatTypeColors[shownType(seat)] } as CSSProperties : undefined
}

function gapPreview(rowId: string, col: number, ghost: boolean): 'insert' | 'remove' | null {
  if (tool.value !== 'gap' || ghost) return null
  const onHeader = hoverHeader.value === col
  const onCell = hover.value?.rowId === rowId && hover.value.col === col
  if (!onHeader && !onCell) return null
  return shiftHeld.value ? 'remove' : 'insert'
}

function cellClass(rowId: string, col: number, seat: GridSeat | undefined, ghost: boolean): Record<string, boolean> {
  const s = stroke.value
  const numbering = s?.tool === 'number' && s.rowId === rowId
  const moving = s?.tool === 'move' && !!moveDelta.value
  const movingFrom = moving && !!seat && (groupMove.value ? selectedSeatIds.value.has(seat.id) : seat.id === s.seatId)
  const gap = gapPreview(rowId, col, ghost)

  return {
    'cell--seat': !!seat,
    'cell--new': !!seat && isNew(seat.id),
    'cell--ghost': ghost,
    'cell--extra': col === colCount.value,
    'cell--hover': hover.value?.rowId === rowId && hover.value.col === col,
    'cell--selected': selectedKeys.value.has(`${rowId}|${col}`),
    'cell--marquee': inMarquee(rowId, col),
    'cell--stroke-row': numbering || (s?.tool === 'move' && !groupMove.value && s.rowId === rowId),
    'cell--preview': numbering && numberPreview.value.has(col),
    'cell--paint-add': s?.tool === 'add' && addPreview.value.has(`${rowId}:${col}`),
    'cell--paint-erase': s?.tool === 'erase' && !!seat && paintedSeats.value.has(seat.id),
    'cell--paint-type': s?.tool === 'type' && !!seat && paintedSeats.value.has(seat.id),
    'cell--moving-from': movingFrom,
    'cell--moving-to': moving && !!moveTargetName(rowId, col),
    // A selected seat would land on one that isn't selected: releasing here is refused
    'cell--moving-blocked': moving && groupMove.value && !!seat && !selectedSeatIds.value.has(seat.id) && !!moveTargetName(rowId, col),
    'cell--gap-insert': gap === 'insert',
    'cell--gap-remove': gap === 'remove',
  }
}

const hoverInfo = computed<string>(() => {
  const h = hover.value
  if (!h) return 'Passe o mouse sobre a grade para ver os detalhes da célula.'
  if (h.rowId === GHOST) return `Nova fila · coluna ${h.col}`

  const row = rowById(h.rowId)
  if (!row) return ''
  const seat = seatAt(row.id, h.col)
  if (!seat) return `Fila ${row.symbol} · coluna ${h.col} · vazia`

  return [
    fullLabel(row, seat),
    `nome ${seat.name}`,
    seat.label ? `rótulo "${seat.label}"` : null,
    `coluna ${seat.position}`,
    SeatTypeLabels[seat.type],
    isNew(seat.id) ? 'não salva' : null,
  ].filter(Boolean).join(' · ')
})

// Strokes -----------------------------------------------------------------------------------------------------------

function startStroke(rowId: string, col: number, seatId: string | null = null) {
  stroke.value = { tool: tool.value, rowId, fromCol: col, lastCol: col, seatId, cells: [], keys: new Set() }
}

/** Records a cell for Adicionar, Apagar or Tipo; nothing changes until the pointer is released */
function paint(rowId: string, col: number) {
  const s = stroke.value
  const key = `${rowId}:${col}`
  if (!s || s.keys.has(key)) return
  s.keys.add(key)

  const exists = rowId === GHOST || !!rowById(rowId)
  const seat = seatAt(rowId, col)

  if (s.tool === 'add') {
    if (exists && !seat) s.cells.push({ rowId, col, seatId: null })
    return
  }
  if (!seat || (s.tool === 'type' && seat.type === paintType.value)) return
  s.cells.push({ rowId, col, seatId: seat.id })
}

function onCellDown(event: PointerEvent, rowId: string, col: number) {
  if (event.button !== 0) return
  shiftHeld.value = event.shiftKey
  suppressClick = false

  // Touch pointers are captured by the cell they start on; release so pointerenter fires on the next cells
  const target = event.currentTarget as HTMLElement
  if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId)

  if (tool.value === 'select') {
    // The selection only changes on click or once the drag reaches another cell: changing it here would let the
    // blur of a panel field apply its value to the newly selected seat
    if (!event.shiftKey) {
      const additive = event.ctrlKey || event.metaKey
      marquee.value = { start: { rowId, col }, end: { rowId, col }, moved: false, base: additive ? seatEntriesOnly() : [] }
    }
    return
  }

  if (tool.value === 'gap') {
    gapAt(rowId, col, event.shiftKey)
    return
  }

  if (tool.value === 'move') {
    const seat = seatAt(rowId, col)
    if (seat) startStroke(rowId, col, seat.id)
    return
  }

  startStroke(rowId, col)
  if (tool.value !== 'number') paint(rowId, col)
}

function onCellEnter(event: PointerEvent, rowId: string, col: number) {
  hover.value = { rowId, col }
  shiftHeld.value = event.shiftKey

  const m = marquee.value
  if (m) {
    m.end = { rowId, col }
    if (m.start.rowId !== rowId || m.start.col !== col) m.moved = true
    if (m.moved) selection.value = union(m.base, rectEntries(m.start, m.end))
    return
  }

  const s = stroke.value
  if (!s) return

  // Numerar and Mover stay in the Row the stroke started on: only the column counts
  if (s.tool === 'number' || s.tool === 'move') s.lastCol = col
  else paint(rowId, col)
}

function onCellClick(event: MouseEvent, rowId: string, col: number) {
  if (tool.value !== 'select') return
  if (suppressClick) {
    suppressClick = false
    return
  }
  selectClick(event, rowId, col)
}

function onPointerUp() {
  const m = marquee.value
  if (m) {
    marquee.value = null
    if (m.moved) {
      suppressClick = true
      anchor.value = m.start
    }
  }

  const s = stroke.value
  if (!s) return
  stroke.value = null

  if (s.tool === 'number') applyNumbering(s)
  else if (s.tool === 'move') applyMove(s)
  else applyPaint(s)
}

/** Where the cells a stroke went over are, for "Última ação" */
function rowsOf(symbols: Set<string>): string {
  return symbols.size ? ` (fila ${[ ...symbols ].join(', ')})` : ''
}

function applyPaint(s: Stroke) {
  if (!s.cells.length) return

  if (s.tool === 'add') {
    const names = addNames(s)
    const symbols = new Set<string>()
    let added = 0
    let created: string | null = null

    editor.batch(`Adicionar ${plural(s.cells.length, 'cadeira', 'cadeiras')}`, () => {
      let ghostRow: string | null = null
      s.cells.forEach((cell) => {
        if (cell.rowId === GHOST && !ghostRow && !editor.addRow(editor.rows.length)) {
          ghostRow = editor.rows[editor.rows.length - 1].id
          created = editor.rows[editor.rows.length - 1].symbol
        }
        const rowId = cell.rowId === GHOST ? ghostRow : cell.rowId
        if (!rowId) return

        if (!editor.addSeat(rowId, cell.col, { name: names.get(`${cell.rowId}:${cell.col}`) })) {
          added++
          symbols.add(rowById(rowId)?.symbol ?? '')
        }
      })
    })
    done(`adicionou ${plural(added, 'cadeira', 'cadeiras')}${rowsOf(symbols)}${created ? `; criou a fila ${created}` : ''}`)
    return
  }

  const ids = s.cells.map(cell => cell.seatId!)
  const symbols = new Set(s.cells.map(cell => rowById(cell.rowId)?.symbol ?? ''))

  if (s.tool === 'erase') {
    report(editor.removeSeats(ids), `apagou ${plural(ids.length, 'cadeira', 'cadeiras')}${rowsOf(symbols)}`)
  } else if (s.tool === 'type') {
    report(editor.setType(ids, paintType.value), `${plural(ids.length, 'cadeira', 'cadeiras')} → ${SeatTypeLabels[paintType.value]}${rowsOf(symbols)}`)
  }
}

/**
 * Names the stroked cells in place: a seat keeps its id, Type and Label and only gets the new name (it is the same
 * physical chair); an empty cell gets a new seat. Seats outside the stroke are untouched.
 */
function applyNumbering(s: Stroke) {
  const [from, to] = strokeRange(s)
  if (from === to) {
    done('Numerar: arraste por pelo menos duas células')
    return
  }

  const names = seriesFor(to - from + 1)
  let symbol = rowById(s.rowId)?.symbol

  const error = s.rowId === GHOST
    ? editor.batch(`Numerar nova fila, colunas ${from}–${to}`, () => {
        const added = editor.addRow(editor.rows.length)
        if (added) return added
        const row = editor.rows[editor.rows.length - 1]
        symbol = row.symbol
        return editor.numberCells(row.id, from, names)
      })
    : editor.numberCells(s.rowId, from, names)

  report(error, `fila ${symbol ?? ''}, colunas ${from}–${to}: ${names[0]} … ${names[names.length - 1]}`)
}

function applyMove(s: Stroke) {
  const delta = s.lastCol - s.fromCol
  if (!delta) return
  // The stroke is already over, so check the group here rather than through `groupMove`
  if (s.seatId && selectedSeatIds.value.has(s.seatId) && selectedSeats.value.length > 1) {
    shiftSelection(delta)
    return
  }

  const row = rowById(s.rowId)
  const seat = row?.seats.find(item => item.id === s.seatId)
  if (!row || !seat || s.lastCol === seat.position) return

  const other = seatAt(row.id, s.lastCol)
  const label = fullLabel(row, seat)
  report(
    editor.updateSeat(seat.id, { position: s.lastCol }),
    `${label} → coluna ${s.lastCol}${other ? ` (trocou com ${fullLabel(row, other)})` : ''}`,
  )
}

// Corredor ----------------------------------------------------------------------------------------------------------

function gapAt(rowId: string | null, col: number, remove: boolean) {
  if (rowId === GHOST) return
  const row = rowId ? rowById(rowId) : undefined
  if (rowId && !row) return

  const where = row ? `na fila ${row.symbol}` : 'na seção inteira'
  if (remove) report(editor.removeGap(col, row?.id), `fechou a coluna ${col} ${where}`)
  else report(editor.insertGap(col, row?.id), `abriu um corredor na coluna ${col} ${where}`)
}

function onHeaderClick(event: MouseEvent, col: number) {
  if (tool.value === 'gap') gapAt(null, col, event.shiftKey)
}

// Rows --------------------------------------------------------------------------------------------------------------

function addRowAt(index: number) {
  const error = editor.addRow(index)
  report(error, error ? '' : `criou a fila ${editor.rows[index].symbol} (vazia)`)
}

const rowMenuOpen: Ref<boolean> = ref(false)
const rowMenuTarget: Ref<HTMLElement | undefined> = ref()
const rowMenuId: Ref<string | null> = ref(null)
const rowSymbol: Ref<string> = ref('')

const menuRow = computed<GridRow | undefined>(() => (rowMenuId.value ? rowById(rowMenuId.value) : undefined))

function onSymbolClick(event: MouseEvent, rowId: string) {
  if (rowId === GHOST) {
    addRowAt(editor.rows.length)
    return
  }

  const row = rowById(rowId)
  if (!row) return
  rowMenuId.value = rowId
  rowSymbol.value = row.symbol
  rowMenuTarget.value = event.currentTarget as HTMLElement
  rowMenuOpen.value = true
}

function renameMenuRow() {
  const row = menuRow.value
  if (!row) return

  const before = row.symbol
  const error = editor.renameRow(row.id, rowSymbol.value)
  report(error, `renomeou a fila ${before} → ${rowSymbol.value.trim().toUpperCase()}`)
  if (!error) rowMenuOpen.value = false
}

function moveMenuRow(delta: number) {
  const row = menuRow.value
  if (!row) return

  editor.moveRow(row.id, delta)
  done(`fila ${row.symbol} agora é a linha ${rowById(row.id)?.order}`)
}

function insertRowNear(offset: number) {
  const row = menuRow.value
  if (!row) return

  const index = editor.rows.findIndex(item => item.id === row.id) + offset
  const error = editor.addRow(index)
  report(error, error ? '' : `criou a fila ${editor.rows[index].symbol} ${offset ? 'atrás' : 'à frente'} da ${row.symbol}`)
  if (!error) rowMenuOpen.value = false
}

function selectMenuRow() {
  const row = menuRow.value
  if (!row) return

  selection.value = row.seats.map(seat => ({ rowId: row.id, seatId: seat.id, col: seat.position }))
  anchor.value = row.seats.length ? { rowId: row.id, col: row.seats[0].position } : null
  tool.value = 'select'
  rowMenuOpen.value = false
}

function removeMenuRow() {
  const row = menuRow.value
  if (!row) return

  const symbol = row.symbol
  report(editor.removeRow(row.id), `removeu a fila ${symbol}`)
  rowMenuOpen.value = false
}

// Keyboard ----------------------------------------------------------------------------------------------------------

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable]')
}

function onKeyDown(event: KeyboardEvent) {
  // A confirmation is open over the editor
  if (dialogStore.isOpen) return

  if (event.key === 'Shift') shiftHeld.value = true
  const typing = isTyping(event.target)

  // Ctrl/Cmd+Z: our undo, except inside a text field where the browser's own undo applies
  if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'z') {
    if (typing) return
    event.preventDefault()
    undo()
    return
  }

  if (rowMenuOpen.value) return

  if (event.key === 'Escape' && typing && event.target instanceof HTMLElement && event.target.closest('.panel')) {
    panel.value?.resetSeatForm()
    event.target.blur()
    clearSelection()
    return
  }
  if (typing || event.ctrlKey || event.metaKey || event.altKey) return

  if (event.shiftKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    if (selectedSeats.value.length) {
      event.preventDefault()
      shiftSelection(event.key === 'ArrowLeft' ? -1 : 1)
    }
    return
  }

  if (event.key === 'Escape') {
    // Vuetify would close the dialog on Escape: the editor keeps it for strokes and the selection
    event.stopPropagation()
    if (stroke.value) {
      stroke.value = null
      done('traço cancelado')
    } else if (marquee.value) {
      marquee.value = null
    } else {
      clearSelection()
    }
    return
  }

  if ((event.key === 'Delete' || event.key === 'Backspace') && selectedSeats.value.length) {
    event.preventDefault()
    removeSelected()
    return
  }

  if (event.key === 'Enter') {
    const h = hover.value
    if (!h || (event.target instanceof HTMLElement && event.target.closest('button'))) return
    event.preventDefault()
    openInPanel(h.rowId, h.col)
    return
  }

  if (tool.value === 'type' && /^[1-4]$/.test(event.key)) {
    paintType.value = seatTypes[Number(event.key) - 1]
    return
  }

  const found = TOOLS.find(item => item.shortcut.toLowerCase() === event.key.toLowerCase())
  if (found) {
    event.preventDefault()
    tool.value = found.key
  }
}

function onKeyUp(event: KeyboardEvent) {
  if (event.key === 'Shift') shiftHeld.value = false
}

function onBlur() {
  shiftHeld.value = false
  onPointerUp()
}

onMounted(() => {
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('pointercancel', onPointerUp)
  window.addEventListener('keydown', onKeyDown, { capture: true })
  window.addEventListener('keyup', onKeyUp)
  window.addEventListener('blur', onBlur)
})

onBeforeUnmount(() => {
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('pointercancel', onPointerUp)
  window.removeEventListener('keydown', onKeyDown, { capture: true })
  window.removeEventListener('keyup', onKeyUp)
  window.removeEventListener('blur', onBlur)
})
</script>

<style scoped>
.seat-editor {
  align-items: flex-start;
}

.palette {
  width: 172px;
  flex: none;
  padding-right: 12px;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.palette__btn {
  justify-content: flex-start;
  letter-spacing: normal;
  font-weight: 500;
}

.palette__btn :deep(.v-btn__content) {
  width: 100%;
  justify-content: flex-start;
}

.palette__icon {
  width: 22px;
  margin-right: 8px;
}

.palette__kbd,
.options-bar__kbd {
  margin-left: auto;
  padding: 1px 5px;
  border: 1px solid currentColor;
  border-radius: 4px;
  font: 600 10px/1.3 ui-monospace, Menlo, monospace;
  opacity: 0.6;
}

.palette__kbd--inline {
  margin-left: 0;
}

.options-bar__kbd {
  margin-left: 6px;
}

.main {
  min-width: 0;
}

.options-bar {
  min-height: 52px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: #fafafa;
  font-size: 13px;
}

.options-bar__hint {
  color: rgba(0, 0, 0, 0.65);
  min-width: 0;
  flex: 1 1 260px;
}

.options-bar__last {
  flex: none;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
}

.grid-wrap {
  overflow: auto;
  padding: 8px 0 16px;
}

.grid {
  display: grid;
  gap: 3px;
  width: max-content;
  margin: 0 auto;
  user-select: none;
  touch-action: none;
}

.stage {
  height: 26px;
  margin-bottom: 10px;
  border-radius: 0 0 40px 40px;
  background: #37474f;
  color: #fff;
  font: 600 12px/26px sans-serif;
  letter-spacing: 0.4em;
  text-align: center;
}

.col-head {
  height: 18px;
  font: 10px/18px ui-monospace, Menlo, monospace;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
  border-radius: 3px;
}

.col-head--extra {
  opacity: 0.4;
}

.col-head--gap {
  cursor: pointer;
  background: rgba(251, 140, 0, 0.08);
}

.col-head--hover {
  background: #fb8c00;
  color: #fff;
}

.row-symbol {
  height: 30px;
  border-radius: 4px;
  font: 600 13px/30px sans-serif;
  color: #37474f;
  text-align: center;
  cursor: pointer;
}

.row-symbol:hover {
  background: rgba(0, 0, 0, 0.06);
}

.row-symbol--ghost {
  color: rgba(0, 0, 0, 0.4);
  font-weight: 400;
}

.row-symbol--ghost-label {
  font-size: 10px;
  font-style: italic;
  text-align: left;
  padding-left: 4px;
}

.cell {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #dde3e6;
  border-radius: 5px;
  font: 600 11px/1 ui-monospace, Menlo, monospace;
  color: #37474f;
}

.cell--ghost,
.cell--extra {
  border-color: #eceff1;
  background: repeating-linear-gradient(45deg, transparent 0 4px, rgba(0, 0, 0, 0.015) 4px 8px);
}

.cell--seat {
  border: 1.5px solid var(--seat-color);
  background: color-mix(in srgb, var(--seat-color) 14%, white);
}

.cell--new {
  border-style: dashed;
  box-shadow: inset 0 0 0 1px #43a04766;
}

.cell--hover {
  outline: 2px solid rgba(25, 118, 210, 0.45);
  outline-offset: 1px;
}

.cell--marquee:not(.cell--seat) {
  background: rgba(25, 118, 210, 0.08);
}

.cell--selected {
  outline: 2.5px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
  z-index: 1;
}

.cell--selected:not(.cell--seat) {
  background: rgba(25, 118, 210, 0.1);
}

.cell--stroke-row:not(.cell--seat) {
  background: rgba(251, 140, 0, 0.06);
}

.cell--preview {
  border: 1.5px solid #fb8c00;
  background: #fff3e0;
}

.cell--paint-add {
  border: 1.5px dashed #43a047;
  background: #e8f5e9;
}

.cell--paint-erase {
  border-color: #e53935;
  background: rgba(229, 57, 53, 0.15);
  opacity: 0.55;
}

.cell--paint-type {
  box-shadow: 0 0 0 2px var(--seat-color);
}

.cell--moving-from {
  opacity: 0.35;
}

.cell--moving-to {
  border: 1.5px solid #1976d2;
  background: #e3f2fd;
}

.cell--moving-blocked {
  border-color: #e53935;
  background: rgba(229, 57, 53, 0.15);
}

.cell--moving-blocked .cell__name--preview {
  color: #c62828;
}

.cell--gap-insert {
  box-shadow: inset 4px 0 0 #fb8c00;
}

.cell--gap-remove {
  background: rgba(229, 57, 53, 0.15);
  border-color: #e53935;
}

.cell__name--preview {
  color: #e65100;
}

.cell--paint-add .cell__name--preview {
  color: #2e7d32;
}

.cell--moving-to .cell__name--preview {
  color: #1565c0;
}

.cell__type {
  position: absolute;
  top: 1px;
  left: 2px;
  font-size: 8px;
}

.cell__dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f57c00;
}

.cell__dot--legend {
  position: static;
  display: inline-block;
}

.grid--select .cell:not(.cell--ghost) {
  cursor: default;
}

.grid--add .cell,
.grid--number .cell {
  cursor: cell;
}

.grid--erase .cell--seat,
.grid--type .cell--seat {
  cursor: crosshair;
}

.grid--gap .cell:not(.cell--ghost) {
  cursor: col-resize;
}

.grid--move .cell--seat {
  cursor: grab;
}

.status {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
}

.status__hover {
  font-family: ui-monospace, Menlo, monospace;
}

.legend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.legend__swatch {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid;
  border-radius: 3px;
  font-size: 8px;
}

.legend__swatch--new {
  border: 1.5px dashed #78909c;
  box-shadow: inset 0 0 0 1px #43a04766;
}
</style>
